/**
 * API client for Momento backend.
 * Automatically detects API URL based on current hostname.
 */
function getApiBaseUrl(): string {
  const hostname = window.location.hostname;

  // Production domain takes priority (even over env var)
  if (hostname.includes('momento-pic.com')) {
    return 'https://api.momento-pic.com';
  }

  // Use env var for local development
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // Fallback for local development
  return 'http://localhost:8100';
}

const API_BASE_URL = getApiBaseUrl();

export { API_BASE_URL };

interface RequestOptions extends RequestInit {
  params?: Record<string, string>;
  timeout?: number; // timeout in milliseconds
}

let unauthorizedHandler: (() => void) | null = null;

/** One place notices a dead session (BUG-069). Every 401 from a real API call
 * lands here so the app can SAY "you're signed out" instead of failing
 * silently behind a screen that still looks signed in. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

/** For transports that don't go through this client — the send outbox has its
 * own fetch — so a dead session still reaches the app-wide handler. */
export function reportUnauthorized(): void {
  unauthorizedHandler?.();
}

/** A 401 from a passkey ceremony or logout means "that attempt failed", not
 * "your session died" — those must never bounce the user to sign-in. */
function noteStatus(endpoint: string, status: number): void {
  if (status !== 401) return;
  if (endpoint.startsWith('/passkey') || endpoint.startsWith('/auth/logout')) {
    return;
  }
  reportUnauthorized();
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    status?: number; // HTTP status — lets callers tell 5xx "server trouble" from 4xx "denied"
  };
  pagination?: {
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const { params, timeout, ...fetchOptions } = options;

    // Build URL with query params
    let url = `${this.baseUrl}${endpoint}`;
    if (params) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }

    // Set default headers
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    };

    // Set up abort controller for timeout
    const controller = new AbortController();
    const timeoutId = timeout ? setTimeout(() => controller.abort(), timeout) : null;

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        headers,
        credentials: 'include', // session rides in the httpOnly cookie
        signal: controller.signal,
      });

      const data = await response.json();

      // Clear timeout on success
      if (timeoutId) clearTimeout(timeoutId);

      if (!response.ok) {
        noteStatus(endpoint, response.status);
        // Backend may return error in 'error' or 'detail' field
        const errorData = data.error || data.detail || {
          code: 'REQUEST_FAILED',
          message: `Request failed with status ${response.status}`,
        };
        return {
          success: false,
          error: {
            ...(typeof errorData === 'string'
              ? { code: 'ERROR', message: errorData }
              : errorData),
            status: response.status,
          },
        };
      }

      return data;
    } catch (error) {
      // Clear timeout on error
      if (timeoutId) clearTimeout(timeoutId);

      // Handle abort/timeout
      if (error instanceof Error && error.name === 'AbortError') {
        return {
          success: false,
          error: {
            code: 'TIMEOUT',
            message: 'Request timed out. The AI is taking longer than expected.',
          },
        };
      }

      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  async get<T>(endpoint: string, options?: { params?: Record<string, string | number>; headers?: Record<string, string>; timeout?: number }) {
    // Convert number params to strings
    const stringParams = options?.params
      ? Object.fromEntries(
          Object.entries(options.params).map(([k, v]) => [k, String(v)])
        )
      : undefined;
    return this.request<T>(endpoint, { method: 'GET', params: stringParams, headers: options?.headers, timeout: options?.timeout });
  }

  async post<T>(endpoint: string, body?: unknown, options?: { timeout?: number }) {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      timeout: options?.timeout,
    });
  }

  async postFormData<T>(endpoint: string, formData: FormData, options?: { timeout?: number }): Promise<{ success: boolean; data?: T; error?: { code: string; message: string } }> {
    // For FormData, we don't set Content-Type - browser will set it with boundary
    const { timeout } = options || {};

    const url = `${this.baseUrl}${endpoint}`;

    const controller = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (timeout) {
      timeoutId = setTimeout(() => controller.abort(), timeout);
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        signal: controller.signal,
      });

      if (timeoutId) clearTimeout(timeoutId);

      const data = await response.json();

      if (!response.ok) {
        noteStatus(endpoint, response.status);
        const errorData = data.error || data.detail || {
          code: 'REQUEST_FAILED',
          message: `Request failed with status ${response.status}`,
        };
        return {
          success: false,
          error: typeof errorData === 'string'
            ? { code: 'ERROR', message: errorData }
            : errorData,
        };
      }

      return data;
    } catch (error) {
      if (timeoutId) clearTimeout(timeoutId);

      if (error instanceof Error && error.name === 'AbortError') {
        return {
          success: false,
          error: { code: 'TIMEOUT', message: 'Request timed out' },
        };
      }

      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  async put<T>(endpoint: string, body?: unknown) {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string) {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  async patch<T>(endpoint: string, body?: unknown) {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * Upload files using FormData.
   * Does not set Content-Type header (browser sets it with boundary).
   *
   * Always settles: every path has a timeout (BUG-055 — a stalled cell link
   * used to hang the promise forever, so the moment stayed "sending" and the
   * offline queue was never reached). Callers with a real blob should pass
   * timeoutMs = computeUploadTimeoutMs(blob.size).
   *
   * options.signal aborts the transfer mid-flight (FEAT-028 wave 3: a
   * discarded capture stops sending its bytes instead of finishing an upload
   * nobody wants). An abort settles as {code: 'ABORTED'}, never a rejection.
   */
  async upload<T>(
    endpoint: string,
    formData: FormData,
    onProgress?: (progress: number) => void,
    options?: { timeoutMs?: number; signal?: AbortSignal }
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    const timeoutMs = options?.timeoutMs ?? 120_000;
    const signal = options?.signal;
    if (signal?.aborted) {
      return {
        success: false,
        error: { code: 'ABORTED', message: 'Upload aborted' },
      };
    }

    // If no progress callback, use simple fetch
    if (!onProgress) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      const onOuterAbort = () => controller.abort();
      signal?.addEventListener('abort', onOuterAbort);
      try {
        const response = await fetch(url, {
          method: 'POST',
          body: formData,
          credentials: 'include',
          signal: controller.signal,
        });

        const data = await response.json();

        if (!response.ok) {
          noteStatus(endpoint, response.status);
          return {
            success: false,
            error: {
              code: 'UPLOAD_FAILED',
              message: `Upload failed with status ${response.status}`,
              ...(data.error || {}),
              status: response.status,
            },
          };
        }

        return data;
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          return signal?.aborted
            ? {
                success: false,
                error: { code: 'ABORTED', message: 'Upload aborted' },
              }
            : {
                success: false,
                error: { code: 'TIMEOUT', message: 'Upload timed out' },
              };
        }
        return {
          success: false,
          error: {
            code: 'NETWORK_ERROR',
            message: error instanceof Error ? error.message : 'Network error',
          },
        };
      } finally {
        clearTimeout(timeoutId);
        signal?.removeEventListener('abort', onOuterAbort);
      }
    }

    // Use XMLHttpRequest for progress tracking
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(data);
          } else {
            noteStatus(endpoint, xhr.status);
            resolve({
              success: false,
              error: {
                code: 'UPLOAD_FAILED',
                message: `Upload failed with status ${xhr.status}`,
                ...(data.error || {}),
                status: xhr.status,
              },
            });
          }
        } catch {
          resolve({
            success: false,
            error: {
              code: 'PARSE_ERROR',
              message: 'Failed to parse response',
              status: xhr.status,
            },
          });
        }
      });

      xhr.addEventListener('error', () => {
        resolve({
          success: false,
          error: {
            code: 'NETWORK_ERROR',
            message: 'Network error during upload',
          },
        });
      });

      xhr.addEventListener('timeout', () => {
        resolve({
          success: false,
          error: { code: 'TIMEOUT', message: 'Upload timed out' },
        });
      });

      xhr.addEventListener('abort', () => {
        resolve({
          success: false,
          error: { code: 'ABORTED', message: 'Upload aborted' },
        });
      });

      if (signal) {
        const onOuterAbort = () => xhr.abort();
        signal.addEventListener('abort', onOuterAbort);
        xhr.addEventListener('loadend', () =>
          signal.removeEventListener('abort', onOuterAbort)
        );
      }

      xhr.open('POST', url);
      xhr.timeout = timeoutMs;
      xhr.withCredentials = true;
      xhr.send(formData);
    });
  }
}

export const api = new ApiClient(API_BASE_URL);

// Health check helper
export async function checkApiHealth() {
  return api.get<{ status: string; environment: string; version: string }>('/health');
}
